
import { FAQ } from "./types";

export const generalFaqs: FAQ[] = [
  {
    question: "What is Upfixe Ads?",
    answer: "Upfixe Ads is an AI-powered ad platform that automates and optimizes Google Ads and Microsoft Advertising (Bing Ads) campaigns. Our platform creates structured 5-angle campaigns, manages keywords, writes RSA ad copy, and enforces mathematical budget limits directly from your landing page URL or conversational AI clients."
  },
  {
    question: "Does Upfixe support Microsoft Advertising (Bing Ads)?",
    answer: "Yes! Upfixe provides native dual-network support for both Google Ads and Microsoft Advertising (Bing Ads). You can create 5-angle campaigns, research keywords, and manage bids across Bing, Yahoo, and Microsoft Copilot to capitalize on 30–50% lower CPCs on corporate desktop searchers."
  },
  {
    question: "How does the AI automation work?",
    answer: "Our AI analyzes your business goals, target audience, and historical performance data to create optimized ad campaigns. It continuously monitors performance and makes real-time adjustments to improve results, including bid adjustments, keyword optimization, and ad copy improvements."
  },
  {
    question: "What businesses are best suited for Upfixe Ads?",
    answer: "Upfixe Ads works for businesses of all sizes, from freelancers to large enterprises. We have specialized solutions for e-commerce businesses, local businesses, startups, agencies, freelancers, and corporate clients. Each solution is tailored to meet the specific needs and challenges of these business types."
  },
  {
    question: "How much does Upfixe Ads cost?",
    answer: "Three tiers, all with a 7-day free trial. Solo $99/month (1 brand) is built for founders. Agency $249/month (5 brands) is for freelancers and consultants. Studio $599/month (25 brands) is for small agencies. Annual billing saves ~17%. The first 50 customers on Solo can lock in €49/month for life via our founder pricing."
  },
  {
    question: "Do I need to have Google or Bing Ads experience to use Upfixe?",
    answer: "No, you don't need prior ad network experience. Upfixe is designed to be intuitive and user-friendly, making it accessible for beginners while still offering advanced features for experienced marketers. Our AI handles the complex technical aspects of ad management."
  }
];

export const whyUseFaqs: FAQ[] = [
  {
    question: "How does Upfixe save time compared to traditional ad management?",
    answer: "Upfixe automates the most time-consuming parts of Google Ads management, saving you an average of 4+ hours per campaign. Our platform handles keyword research, ad group organization, ad copy creation, and performance monitoring automatically, freeing you to focus on strategy and growth."
  },
  {
    question: "What makes Upfixe's AI different from other advertising tools?",
    answer: "Unlike generic AI tools, our AI assistants are custom-trained on your specific brand's value proposition and goals. Each brand gets its own dedicated AI brain that understands your unique selling points, target audience, and industry context, resulting in more relevant campaigns and better results."
  },
  {
    question: "How does Upfixe improve campaign performance?",
    answer: "Our AI judges keyword effectiveness against your value proposition before spending a single dollar, eliminating irrelevant keywords that waste budget. We also organize keywords into logical ad groups with tailored ad copy, improving quality scores and conversion rates while lowering cost-per-acquisition."
  },
  {
    question: "Why is Upfixe better for agencies and freelancers?",
    answer: "Upfixe enables you to manage multiple clients from a single dashboard with brand-specific AI assistants for each client. You can automatically generate client-ready reports, scale your ad management without adding staff, and deliver professionally optimized campaigns even with limited resources."
  },
  {
    question: "How does Upfixe ensure transparency in ad management?",
    answer: "Unlike black-box solutions, Upfixe provides complete visibility through Google Sheets that detail every keyword, ad group, and ad copy before campaigns go live. You maintain full control to review and edit everything, plus receive detailed weekly performance reports comparing current results to previous periods."
  }
];

export const platformFaqs: FAQ[] = [
  {
    question: "How do I get started with Upfixe Ads?",
    answer: "Sign up at upfixe.com and start a 7-day free trial on any tier. Connect your Google Ads account via one OAuth screen, add Upfixe to Claude / ChatGPT / Perplexity, then describe your business in chat. Your first campaign is usually published (paused) within four minutes."
  },
  {
    question: "What are 'brands' in Upfixe Ads?",
    answer: "Brands are separate entities you can manage in your account. If you're an agency or freelancer, these will be your different clients. If you're a company, these might represent different product lines, making it perfect for product marketing managers."
  },
  {
    question: "How do I create a brand?",
    answer: "When creating a brand, you'll upload a value proposition document detailing what the brand does, who it sells to, preferred language, etc. Our system uses this to create a dedicated AI assistant that will manage all aspects of this brand within the platform."
  },
  {
    question: "How do I create campaigns?",
    answer: "Once your brand is set up, you can create campaigns by filling out a form with details like budget, target CPA, and target country. Our AI will handle the rest of the campaign creation process based on these inputs."
  },
  {
    question: "Why can I only target one country per campaign?",
    answer: "We limit campaigns to one country at a time because the best framework for Google Ads is to have one campaign per country. This approach makes management and analysis much easier and typically leads to better performance."
  },
  {
    question: "Can I manage Microsoft Advertising (Bing Ads) and Google Ads simultaneously?",
    answer: "Yes! Upfixe allows you to link Google Ads Customer IDs and Microsoft Advertising Account/Customer IDs to each of your brands. You can generate synchronized campaigns for both platforms or choose to publish to either network independently."
  }
];

export const keywordsFaqs: FAQ[] = [
  {
    question: "How does keyword generation work?",
    answer: "Keywords are initially seeded from your target URL. Your AI assistant then judges the effectiveness of these search terms against your value proposition document. This means you get Google working for you, followed by AI optimization for the best results."
  },
  {
    question: "What if my website isn't well-optimized for keywords?",
    answer: "If your webpage is poorly optimized (resulting in bad keywords), the system will use the positive keywords you entered in the campaign form as a backup. Think of these as alternatives if your target URL doesn't provide good keywords. The system will then seed more keywords from your suggestions."
  },
  {
    question: "What's the difference between 'cold' and 'branded' campaigns?",
    answer: "For cold campaigns, we use your target URL and positive keywords to find new customers. For branded campaigns, we use negative keywords to refine targeting. For example, if you're Volkswagen, 'VW', 'Jetta', or 'Beetle' would be branded/negative terms in a cold campaign, but target terms in a branded campaign."
  },
  {
    question: "How are ad groups created?",
    answer: "We analyze generated keywords against your campaign goal and value proposition document. The system divides them into logical ad groups that categorize similar terms, ensuring that the ad copy for each group is relevant to the search terms it contains."
  },
  {
    question: "What types of ads does Upfixe create?",
    answer: "Currently, Upfixe only creates search term ads. We may expand to other ad types in the future."
  }
];

export const implementationFaqs: FAQ[] = [
  {
    question: "How do I review and implement campaigns?",
    answer: "Once your campaign is created, you'll receive a Google Sheet with detailed information on ad groups, search terms, and ad copy. You can share this with your team or clients, and make any edits as needed. If you've connected your Google account with posting permissions, you can simply press 'approve' to upload the campaign directly to Google."
  },
  {
    question: "What happens if something goes wrong during upload?",
    answer: "If any issues occur during the Google Ads upload process, you'll be notified in the portal and can restart the process. Our system is designed to minimize errors, but we provide clear error reporting if they do occur."
  },
  {
    question: "How are budgets and CPA targets handled?",
    answer: "Budgets and CPA targets are set in the local currency of your target country. For example, if you're targeting the US, values are in dollars; for Europe, they're in euros, etc."
  },
  {
    question: "What reporting does Upfixe provide?",
    answer: "Every Monday, you'll receive a report for each of your brands that compares the most recent week to the previous week across all campaigns. This report is available in the portal and can be forwarded to clients as needed."
  },
  {
    question: "What's on your product roadmap?",
    answer: "Our immediate roadmap includes making reports actionable—we'll add or remove keywords, change budgets, and create new ad groups based on performance. If our Google Cloud project is approved, we'll also introduce AI campaign implementation that analyzes your Google Analytics for conversions and traffic to suggest new campaign ideas."
  }
];

export const supportFaqs: FAQ[] = [
  {
    question: "Do you offer customer support?",
    answer: "Yes — email george@upfixe.com and you'll hear back from the founder. Studio customers get priority response. We also publish documentation and guides for the common flows."
  },
  {
    question: "Can I cancel my subscription anytime?",
    answer: "Yes, cancel any time from your dashboard. There are no long-term contracts or cancellation fees. Your access continues until the end of your current billing period."
  },
  {
    question: "Can I use Upfixe for multiple brands or clients?",
    answer: "Yes — that's what the Agency (5 brands) and Studio (25 brands) tiers are built for. Each brand has its own value-prop doc, its own Google Ads sub-account binding, and its own reporting. Need more than 25 brands? Email george@upfixe.com for enterprise pricing."
  },
  {
    question: "How do I connect my Google Ads account?",
    answer: "You can connect your Google Ads account in a few simple steps through our dashboard. Navigate to the Settings section, select 'Connections', and follow the prompts to authorize Upfixe with your Google Ads account. We have detailed guides available if you need assistance."
  },
  {
    question: "What kind of results can I expect?",
    answer: "While results vary depending on various factors, our clients typically see improved campaign performance, higher conversion rates, and better ROI compared to manually managed campaigns. Our AI continuously optimizes your campaigns to deliver the best possible results."
  }
];

// Export the FAQ type for use in other files
export type { FAQ };
